/* eslint-disable */
import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSelect from "@ui-components/Select/GenericSelect";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import { createReflectorLicense, updateReflectorLicense, fetchCustomersWithTenant, fetchPartnersWithTenant } from "../../Reflector/request/request";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";

interface LicensePrefill {
  id?: number;
  tenantId?: number;
  serial?: string;
  description?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  total?: number;
  available?: number;
  status?: number;
  activated?: number;
}

interface AddLicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: any) => void;
  licenseTypes: { value: string; label: string }[];
  prefill?: LicensePrefill | null;
}

const AddLicenseModal: React.FC<AddLicenseModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  licenseTypes,
  prefill,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const userProfile = useAppSelector((state) => state.userProfile);
  const isGlobalAdmin = userProfile.TenantType === "GLOBAL_ADMIN";
  const { tenantId: currentTenantId } = useAppSelector(state => state.selection);
  const [tenant, setTenant] = useState("");
  const [description, setDescription] = useState("");
  const [licenseType, setLicenseType] = useState(licenseTypes[0]?.value || "");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [numDevices, setNumDevices] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entities, setEntities] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  
  // Check if we're in edit mode (prefill has an id)
  const isEditMode = prefill?.id !== undefined;

  // Clear error when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  // Clear error when prefill changes (different license selected)
  useEffect(() => {
    setError(null);
  }, [prefill?.id]);

  // Fetch customers and/or partners when modal opens
  useEffect(() => {
    if (isOpen && currentTenantId) {
      fetchEntitiesData();
    }
  }, [isOpen, currentTenantId, isGlobalAdmin]);

  const fetchEntitiesData = async () => {
    setIsLoadingEntities(true);
    try {
      const token = await getAccessTokenSilently();
      let combined: { value: string; label: string }[] = [];
      // Always fetch customers
      const customersRes = await fetchCustomersWithTenant(token, currentTenantId);
      const customers = customersRes.data.data.map((customer: any) => ({
        value: customer.id.toString(),
        label: `Customer: ${customer.nameDisplay || customer.name}`
      }));
      combined = [...customers];
      if (isGlobalAdmin) {
        const partnersRes = await fetchPartnersWithTenant(token);
        const partners = partnersRes.data.data.map((partner: any) => ({
          value: partner.id.toString(),
          label: `Partner: ${partner.nameDisplay || partner.name}`
        }));
        combined = [...customers, ...partners];
      }
      setEntities(combined);
      if (combined.length > 0 && !tenant) {
        setTenant(combined[0].value);
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError('Failed to load tenants');
    } finally {
      setIsLoadingEntities(false);
    }
  };

  useEffect(() => {
    if (prefill) {
      if (prefill.tenantId) setTenant(String(prefill.tenantId));
      if (prefill.description) setDescription(prefill.description);
      if (prefill.type) setLicenseType(prefill.type);
      if (prefill.startDate) setStartDate(new Date(prefill.startDate));
      if (prefill.endDate) setEndDate(new Date(prefill.endDate));
      if (prefill.total !== undefined) setNumDevices(String(prefill.total));
    }
  }, [prefill]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Get the access token
      const token = await getAccessTokenSilently();

      if (isEditMode && prefill?.id) {
        // Edit mode: Update existing license
        if (!currentTenantId) {
          setError("Tenant ID is required to update license");
          return;
        }
        
        // Include status and activated from backend response (prefill)
        // available is not sent in update payload (managed by backend)
        const payload = {
          description: description.trim(),
          total: parseInt(numDevices) || 0,
          startDate: startDate?.toISOString() || "",
          endDate: endDate?.toISOString() || "",
          type: licenseType,
          status: prefill.status ?? 1, // From backend response
          activated: prefill.activated ?? 0 // From backend response
        };
        
        await updateReflectorLicense(token, prefill.id, payload, currentTenantId);
        
        // Call the onCreate callback to refresh the list
        onCreate({});
      } else {
        // Create mode: Create new license
        const payload = {
          tenantId: parseInt(tenant),
          description: description.trim(),
          type: licenseType,
          startDate: startDate?.toISOString() || "",
          endDate: endDate?.toISOString() || "",
          numberOfDevices: parseInt(numDevices) || 0
        };

        const response = await createReflectorLicense(token, payload);
        
        // Call the onCreate callback with the response data
        onCreate(response.data);
      }
      
      // Close the modal on success
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || (isEditMode ? "Failed to update license" : "Failed to create license"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={prefill ? "Edit License" : "Add License"} size="lg">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {error && (
          <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {!isEditMode && (
          <GenericSelect
            label="Customer"
            options={entities}
            onSelect={setTenant}
            defaultValue={tenant}
            placeholder={isLoadingEntities ? "Loading tenants..." : "Select tenant"}
          />
        )}
        {isEditMode && (
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-1">Customer</label>
            <div className="px-3 py-2 bg-semantic-bg-secondary border border-semantic-border-secondary rounded text-semantic-text-secondary">
              {entities.find(e => e.value === tenant)?.label || tenant}
            </div>
          </div>
        )}
        <GenericSelect
          label="License Type"
          options={licenseTypes}
          onSelect={setLicenseType}
          defaultValue={licenseType}
          placeholder="Select license type"
        />

        <GenericDateTimeRangePicker
          label="Start Date"
          value={{ start: startDate, end: null }}
          onChange={(val) => setStartDate(val.start)}
          singleDate
        />
        <GenericDateTimeRangePicker
          label="End Date"
          value={{ start: endDate, end: null }}
          onChange={(val) => setEndDate(val.start)}
          singleDate
        />
        <GenericInput
          label="Description"
          value={description}
          onChange={setDescription}
          placeholder="Description"
          required
        />
        
        <GenericInput
          label="Number of Devices"
          value={numDevices}
          onChange={setNumDevices}
          type="number"
          placeholder="Number of devices"
          required
        />
        <div className="md:col-span-2 flex justify-end mt-4">
          <GenericButton
            label={isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update License" : "Create License")}
            type="submit"
            variant="brand"
            size="md"
            disabled={isLoading || isLoadingEntities}
            loading={isLoading}
          />
        </div>
      </form>
    </GenericModal>
  );
};

export default AddLicenseModal;
